import React, { useEffect } from "react";
import { MainNav } from "@/components/MainNav";
import { PageSEO } from "@/components/PageSEO";
import { ChartTabs } from "@/components/chart-page";
import { isSSR } from "@/lib/isSSR";
import { createServiceSchema, createBreadcrumbsSchema } from '@/utils/schemaUtils';
import { useStore } from "@/store/useStore";
import { TimePeriod, MetalPrice } from "@/lib/metal-price";
import { getPeriodText } from "@/lib/utils";
import { PriceChart } from "@/components/PriceChart";

function ChartsContent() {
  const {
    isHistoryLoading,
    error,
    goldPriceHistory,
    silverPriceHistory,
    selectedTimePeriod,
    fetchPriceHistoryData,
  } = useStore();

  const handleRetryLoading = () => {
    fetchPriceHistoryData(selectedTimePeriod);
  };

  const noData = !isHistoryLoading && goldPriceHistory.length === 0 && silverPriceHistory.length === 0;
  
  const validGoldHistory = goldPriceHistory.filter((p): p is MetalPrice => p.price != null && p.timestamp != null && p.change != null && p.changePercentage != null);
  const validSilverHistory = silverPriceHistory.filter((p): p is MetalPrice => p.price != null && p.timestamp != null && p.change != null && p.changePercentage != null);


  return (
    <>
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Grafici Prezzi Metalli
        </h1>
        <p className="text-muted-foreground">
          Visualizza l'andamento storico dei prezzi di oro e argento
        </p>
      </div>
      
      <PriceChart
        title="Andamento Combinato Oro e Argento"
        className="mb-10"
        isHomePage={false}
      />
      
      <ChartTabs
        isLoading={isHistoryLoading}
        error={!!error}
        noData={noData}
        goldPriceHistory={validGoldHistory}
        silverPriceHistory={validSilverHistory}
        selectedTimePeriod={selectedTimePeriod}
        handleRetryLoading={handleRetryLoading}
        getPeriodText={getPeriodText}
      />
    </>
  );
}

const Charts = () => {
  // Create schema for chart service
  const chartServiceSchema = createServiceSchema({
    name: "Grafici Prezzi Oro e Argento",
    description: "Monitora l'andamento dei prezzi di oro e argento con grafici dettagliati e aggiornati in tempo reale. Visualizza le tendenze giornaliere, settimanali, mensili e annuali.",
    url: "https://www.gildy.it/grafici",
    provider: {
      name: "Gildy",
      url: "https://www.gildy.it"
    },
    areaServed: "Italia"
  });
  
  // Create breadcrumbs schema
  const breadcrumbsSchema = createBreadcrumbsSchema([
    { name: 'Home', url: '/' },
    { name: 'Grafici', url: '/grafici' }
  ]);
  
  // Keywords for SEO
  const keywords = [
    "grafici oro", "grafici argento", "andamento prezzo oro", 
    "storico prezzi oro", "previsioni oro", "trend oro", 
    "analisi prezzo oro", "quotazione oro storica", "grafico oro tempo reale"
  ];
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageSEO 
        title="Grafici Oro e Argento in Tempo Reale - Gildy"
        description="Monitora l'andamento dei prezzi di oro e argento con grafici dettagliati e aggiornati in tempo reale. Visualizza le tendenze giornaliere, settimanali, mensili e annuali."
        canonicalPath="/grafici"
        structuredData={[chartServiceSchema, breadcrumbsSchema]}
        keywords={keywords}
      />

      <MainNav />
      <main className="flex-1 container mx-auto py-8 px-4 md:px-6">
        <ChartsContent />
      </main>
    </div>
  );
};

export default Charts;
